<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useAccessPermissionStore } from "@/stores/access-permission";
import {
  AccessPermissionDataPayload,
  AccessPermissionDataResponse,
} from "@/types/interface/access-permission";
import { mapOptionMultiselect } from "@/utils/helper";
import { useModuleStore } from "@/stores/master/module";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

const idAccessPermission = ref<string>("");
const listModule = ref([]);
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const accessPermissionStore = useAccessPermissionStore();
const moduleStore = useModuleStore();
const detailAccessPermission = ref<AccessPermissionDataResponse>();

const validationSchema = toTypedSchema(
  zod.object({
    name: zod
      .string()
      .nonempty({ message: "Access Permission Name is required" }),
    module: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: module } = useField<string>("module");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadAccessPermission: AccessPermissionDataPayload = {
    name: name.value,
    module_id: module.value?.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await accessPermissionStore.dispatchUpdate(
      idAccessPermission.value,
      payloadAccessPermission
    );
  } else {
    responseApi.value = await accessPermissionStore.dispatchCreate(
      payloadAccessPermission
    );
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Access Permission Successfully Updated"
          : "Access Permission Successfully Added"
      );
      router.push("/master/access-permission");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadListModule = async () => {
  const { success } = await moduleStore.dispatchGetAll(0, 99, "");
  listModule.value = mapOptionMultiselect(
    moduleStore.modules.items,
    "id",
    "name"
  );
};

const loadDetailAccessPermission = async () => {
  const { success, message } = await accessPermissionStore.dispatchDetail(
    idAccessPermission.value
  );
  if (success) {
    detailAccessPermission.value = await accessPermissionStore.category?.data;
    name.value = detailAccessPermission.value?.name;
    module.value = mapOptionMultiselect(detailAccessPermission?.value?.module);
  }
};

onMounted(() => {
  loadListModule();
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idAccessPermission.value = router.currentRoute.value.params.id.toString();
    loadDetailAccessPermission();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Access Permission</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/access-permission"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Access Permission
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="category_code"
                    class="form-label label text-secondary"
                    >Module <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="module"
                      :options="listModule"
                      placeholder="Choose Module"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.module }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Access Permission Name
                    <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Access Permission Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
